import { Directive, ElementRef, Renderer2, HostListener, Input, AfterViewInit } from '@angular/core';

@Directive({
  selector: '[cygovEllipsisTooltip]',
  standalone: true,
})
export class EllipsisTooltipDirective implements AfterViewInit {
  @Input() tooltipText: string = ''; // Text for the tooltip
  @Input() tooltipPlacement: string = 'top'; // Tooltip placement (default: top)
  @Input() tooltipBackgroundColor: string = '#12161A'; // Tooltip background color
  @Input() tooltipTextColor: string = '#C5C9D1'; // Tooltip text color
  @Input() tooltipFontClass: string = 'lato-12-n-vw'; // Font size class
  @Input() tooltipWidth: string = '12vw'; // Tooltip width
  @Input() tooltipPadding: string = '0.3vw'; // Tooltip padding
  @Input() tooltipBorderRadius: string = '4px'; // Tooltip border radius

  private tooltipElement: HTMLElement | null = null;
  private isInitialized = false;
  private originalCursor: string = '';

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    // Store original cursor style
    this.originalCursor = getComputedStyle(this.el.nativeElement).cursor;

    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.initializeEllipsis();
      this.isInitialized = true;
    }, 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isInitialized) {
      this.initializeEllipsis();
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.isInitialized && this.isEllipsisActive()) {
      this.showTooltip();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideTooltip();
  }

  private initializeEllipsis(): void {
    // Force element to have the necessary styles for ellipsis detection
    this.renderer.setStyle(this.el.nativeElement, 'display', 'block');
    this.renderer.setStyle(this.el.nativeElement, 'text-overflow', 'ellipsis');
    this.renderer.setStyle(this.el.nativeElement, 'overflow', 'hidden');
    this.renderer.setStyle(this.el.nativeElement, 'white-space', 'nowrap');

    // Update cursor based on ellipsis state
    this.updateCursor();

    // Force reflow to ensure measurements are accurate
    // eslint-disable-next-line no-unused-expressions
    this.el.nativeElement.offsetHeight;
  }

  private updateCursor(): void {
    if (this.isEllipsisActive()) {
      this.renderer.setStyle(this.el.nativeElement, 'cursor', 'pointer');
    } else {
      this.renderer.setStyle(this.el.nativeElement, 'cursor', this.originalCursor || 'default');
    }
  }

  private isEllipsisActive(): boolean {
    const element = this.el.nativeElement;
    const isOverflowing = element.scrollWidth > element.offsetWidth;
    return isOverflowing;
  }

  private showTooltip(): void {
    if (!this.tooltipElement) {
      // Create main tooltip container
      this.tooltipElement = this.renderer.createElement('div');
      this.renderer.addClass(this.tooltipElement, 'globalControl-tooltip');
      this.renderer.setStyle(this.tooltipElement, 'position', 'absolute');
      this.renderer.setStyle(this.tooltipElement, 'z-index', '1000');
      this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
      this.renderer.setStyle(this.tooltipElement, 'opacity', '1');

      // Create tooltip inner container
      const tooltipInner = this.renderer.createElement('div');
      this.renderer.addClass(tooltipInner, 'tooltip-inner');

      // Apply customizable styles
      this.renderer.setStyle(tooltipInner, 'min-width', this.tooltipWidth);
      this.renderer.setStyle(tooltipInner, 'width', this.tooltipWidth);
      this.renderer.setStyle(tooltipInner, 'max-width', this.tooltipWidth);
      this.renderer.setStyle(tooltipInner, 'padding', this.tooltipPadding);
      this.renderer.setStyle(tooltipInner, 'background-color', this.tooltipBackgroundColor);
      this.renderer.setStyle(tooltipInner, 'color', this.tooltipTextColor);
      this.renderer.setStyle(tooltipInner, 'border-radius', this.tooltipBorderRadius);
      this.renderer.setStyle(tooltipInner, 'text-align', 'left');
      this.renderer.setStyle(tooltipInner, 'word-wrap', 'break-word');
      this.renderer.setStyle(tooltipInner, 'white-space', 'normal');

      // Create tooltip container
      const tooltipContainer = this.renderer.createElement('div');
      this.renderer.addClass(tooltipContainer, 'tooltip-container');

      // Create text element and apply font class
      const textElement = this.renderer.createElement('span');
      this.renderer.addClass(textElement, this.tooltipFontClass);

      // Create text content
      const textContent = this.tooltipText || this.el.nativeElement.textContent || '';
      const textNode = this.renderer.createText(textContent);

      // Append elements
      this.renderer.appendChild(textElement, textNode);
      this.renderer.appendChild(tooltipContainer, textElement);
      this.renderer.appendChild(tooltipInner, tooltipContainer);
      this.renderer.appendChild(this.tooltipElement, tooltipInner);
      this.renderer.appendChild(document.body, this.tooltipElement);

      // Position tooltip
      this.positionTooltip();
    }
  }

  private positionTooltip(): void {
    if (!this.tooltipElement) {
      return;
    }

    const rect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    let top: number;
    let left: number;

    switch (this.tooltipPlacement) {
      case 'right':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.right + 10;
        break;
      case 'left':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.left - tooltipRect.width - 10;
        break;
      case 'bottom':
        top = rect.bottom + 10;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      default: // top
        top = rect.top - tooltipRect.height - 10;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 0) {
      left = 10;
    }
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 10;
    }
    if (top < 0) {
      top = rect.bottom + 10;
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = rect.top - tooltipRect.height - 10;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }

  private hideTooltip(): void {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }
}
